'use client';

import { LazyTabLabECharts } from '@repo/ui';
import type { EChartsOption, BarSeriesOption, LineSeriesOption } from 'echarts';
import { calculateMin, calculateMax, calculateMean, calculateMedian, calculateStandardDeviation } from '../../../lib/stats';

export interface DistributionLabel {
  value: number;
  text: string;
  color?: string;
  lineStyle?: 'solid' | 'dashed' | 'dotted';
}

export interface DistributionGraphProps {
  data: number[];
  labels?: DistributionLabel[];
  title?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  binCount?: number;
  height?: number;
  width?: number;
  showMean?: boolean;
  showMedian?: boolean;
  showStdDev?: boolean;
  showMode?: boolean;
  className?: string;
  xAxisValues?: number[];
  clip?: boolean;
  grid?: {
    left?: string | number;
    right?: string | number;
    top?: string | number;
    bottom?: string | number;
  };
}

// For discrete or continuous probability distributions that use bars/histograms
export function DiscreteDistributionGraph({
  data,
  labels = [],
  title = '',
  xAxisLabel = '',
  yAxisLabel = 'Probability',
  binCount = 20,
  height = 400,
  width,
  showMean = false,
  showMedian = false,
  showStdDev = false,
  className = '',
  xAxisValues,
  clip = false,
  grid
}: DistributionGraphProps) {
  // Early return if data is undefined or empty
  if (!data || data.length === 0) {
    return (
      <div style={{ height }} className={className}>
        <div className="flex items-center justify-center h-full text-gray-500">
          No data available
        </div>
      </div>
    );
  }

  // If xAxisValues are provided, use them directly instead of calculating bins
  if (xAxisValues && xAxisValues.length === data.length) {
    const histogramData = data.map((value, i) => [xAxisValues[i], value]);
    
    // Add statistical markers if requested
    const statisticalLabels: DistributionLabel[] = [];
    
    if (showMean) {
      const mean = calculateMean({ data });
      statisticalLabels.push({
        value: mean,
        text: `Mean: ${mean.toFixed(2)}`,
        color: '#ff4d4d',
        lineStyle: 'solid'
      });
    }

    if (showMedian) {
      const median = calculateMedian({ data });
      statisticalLabels.push({
        value: median,
        text: `Median: ${median.toFixed(2)}`,
        color: '#4d4dff',
        lineStyle: 'dashed'
      });
    }

    if (showStdDev) {
      const mean = calculateMean({ data });
      const stdDev = calculateStandardDeviation({ data });
      statisticalLabels.push(
        {
          value: mean - stdDev,
          text: `-1σ (${(mean - stdDev).toFixed(2)})`,
          color: '#666666',
          lineStyle: 'dotted'
        },
        {
          value: mean + stdDev,
          text: `+1σ (${(mean + stdDev).toFixed(2)})`,
          color: '#666666',
          lineStyle: 'dotted'
        }
      );
    }

    // Combine user-provided labels with statistical labels
    const allLabels = [...labels, ...statisticalLabels];

    // Create mark lines for all labels
    const markLines = allLabels.map(label => ({
      name: label.text,
      xAxis: label.value,
      label: {
        formatter: label.text,
        position: 'insideEndTop' as const,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        padding: [4, 8],
        borderRadius: 4
      },
      lineStyle: {
        color: label.color || '#666',
        type: label.lineStyle || 'solid',
        width: 2
      }
    }));

    const barSeries: BarSeriesOption = {
      type: 'bar',
      data: histogramData,
      barWidth: '99%',
      itemStyle: {
        color: '#8884d8',
        opacity: 0.8,
        borderRadius: [4, 4, 0, 0]
      },
      emphasis: {
        itemStyle: {
          opacity: 1
        }
      },
      markLine: {
        symbol: ['none', 'none'],
        data: markLines,
        emphasis: {
          lineStyle: {
            width: 3
          }
        }
      },
      clip: clip
    };

    const option: EChartsOption = {
      title: {
        text: title,
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderWidth: 1,
        borderColor: '#ccc',
        padding: 10,
        textStyle: {
          color: '#333'
        }
      },
      toolbox: {
        show: true,
        feature: {
          saveAsImage: {
            title: 'Save as Image',
            name: 'distribution-plot'
          },
          dataZoom: {
            title: {
              zoom: 'Zoom',
              back: 'Zoom Reset'
            }
          }
        }
      },
      grid: {
        top: 60,
        right: 40,
        bottom: 60,
        left: 60,
        containLabel: true,
        ...grid
      },
      xAxis: {
        type: 'value',
        name: xAxisLabel,
        nameLocation: 'middle',
        nameGap: 30,
        scale: true,
        axisLine: {
          show: true
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            opacity: 0.3
          }
        }
      },
      yAxis: {
        type: 'value',
        name: yAxisLabel,
        nameLocation: 'middle',
        nameGap: 40,
        axisLine: {
          show: true
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            opacity: 0.3
          }
        }
      },
      series: [barSeries]
    };

    return (
      <div style={{ height }} className={className}>
        <LazyTabLabECharts option={option} themeName="gentle-photon-stream" />
      </div>
    );
  }

  // Original binning logic for when xAxisValues are not provided
  // Calculate statistics using our stats library
  const min = calculateMin({ data });
  const max = calculateMax({ data });
  
  // Dynamically adjust bin count for small datasets
  const adjustedBinCount = Math.min(binCount, Math.max(5, Math.ceil(data.length / 2)));
  const binWidth = (max - min) / adjustedBinCount;
  
  // Create bins
  const bins = Array(adjustedBinCount).fill(0);
  data.forEach(value => {
    const binIndex = Math.min(Math.floor((value - min) / binWidth), adjustedBinCount - 1);
    bins[binIndex]++;
  });

  // Create bin boundaries for x-axis
  const binBoundaries = Array(adjustedBinCount + 1).fill(0).map((_, i) => min + i * binWidth);

  // Convert bins to format expected by ECharts
  const histogramData = bins.map((count, i) => {
    const start = binBoundaries[i];
    const end = binBoundaries[i + 1];
    if (start === undefined || end === undefined) return [0, 0];
    return [(start + end) / 2, count];
  });

  // Add statistical markers if requested
  const statisticalLabels: DistributionLabel[] = [];
  
  if (showMean) {
    const mean = calculateMean({ data });
    statisticalLabels.push({
      value: mean,
      text: `Mean: ${mean.toFixed(2)}`,
      color: '#ff4d4d',
      lineStyle: 'solid'
    });
  }

  if (showMedian) {
    const median = calculateMedian({ data });
    statisticalLabels.push({
      value: median,
      text: `Median: ${median.toFixed(2)}`,
      color: '#4d4dff',
      lineStyle: 'dashed'
    });
  }

  if (showStdDev) {
    const mean = calculateMean({ data });
    const stdDev = calculateStandardDeviation({ data });
    statisticalLabels.push(
      {
        value: mean - stdDev,
        text: `-1σ (${(mean - stdDev).toFixed(2)})`,
        color: '#666666',
        lineStyle: 'dotted'
      },
      {
        value: mean + stdDev,
        text: `+1σ (${(mean + stdDev).toFixed(2)})`,
        color: '#666666',
        lineStyle: 'dotted'
      }
    );
  }

  // Combine user-provided labels with statistical labels
  const allLabels = [...labels, ...statisticalLabels];

  // Create mark lines for all labels
  const markLines = allLabels.map(label => ({
    name: label.text,
    xAxis: label.value,
    label: {
      formatter: label.text,
      position: 'insideEndTop' as const,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      padding: [4, 8],
      borderRadius: 4
    },
    lineStyle: {
      color: label.color || '#666',
      type: label.lineStyle || 'solid',
      width: 2
    }
  }));

  const barSeries: BarSeriesOption = {
    type: 'bar',
    data: histogramData,
    barWidth: '99%',
    itemStyle: {
      color: '#8884d8',
      opacity: 0.8,
      borderRadius: [4, 4, 0, 0]
    },
    emphasis: {
      itemStyle: {
        opacity: 1
      }
    },
    markLine: {
      symbol: ['none', 'none'],
      data: markLines,
      emphasis: {
        lineStyle: {
          width: 3
        }
      }
    },
    clip: clip
  };

  const option: EChartsOption = {
    title: {
      text: title,
      left: 'center',
      top: 10,
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      },
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderWidth: 1,
      borderColor: '#ccc',
      padding: 10,
      textStyle: {
        color: '#333'
      },
      formatter: (params: any) => {
        const binCenter = params[0].data[0];
        const count = params[0].data[1];
        const binStart = binCenter - binWidth / 2;
        const binEnd = binCenter + binWidth / 2;
        return `Range: ${binStart.toFixed(2)} - ${binEnd.toFixed(2)}<br/>Count: ${count}`;
      }
    },
    toolbox: {
      show: true,
      feature: {
        saveAsImage: {
          title: 'Save as Image',
          name: 'distribution-plot'
        },
        dataZoom: {
          title: {
            zoom: 'Zoom',
            back: 'Zoom Reset'
          }
        }
      }
    },
    grid: {
      top: 60,
      right: 40,
      bottom: 60,
      left: 60,
      containLabel: true,
      ...grid
    },
    xAxis: {
      type: 'value',
      name: xAxisLabel,
      nameLocation: 'middle',
      nameGap: 30,
      scale: true,
      axisLine: {
        show: true
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed',
          opacity: 0.3
        }
      }
    },
    yAxis: {
      type: 'value',
      name: yAxisLabel,
      nameLocation: 'middle',
      nameGap: 40,
      axisLine: {
        show: true
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed',
          opacity: 0.3
        }
      }
    },
    series: [barSeries]
  };

  return (
    <div style={{ height }} className={className}>
      <LazyTabLabECharts option={option} themeName="gentle-photon-stream" />
    </div>
  );
}

// For continuous probability distributions that use smooth lines
export function ContinuousDistributionGraph({
  data,
  labels = [],
  title = '',
  xAxisLabel = '',
  yAxisLabel = 'Probability',
  height = 400,
  width,
  showMean = false,
  className = '',
  xAxisValues
}: DistributionGraphProps) {
  // Early return if data is undefined or empty
  if (!data || data.length === 0 || !xAxisValues) {
    return (
      <div style={{ height }} className={className}>
        <div className="flex items-center justify-center h-full text-gray-500">
          No data available
        </div>
      </div>
    );
  }

  const lineData = data.map((value, i) => [xAxisValues[i], value]);

  // Add statistical markers if requested
  const statisticalLabels: DistributionLabel[] = [];
  
  if (showMean) {
    const mean = calculateMean({ data: xAxisValues });
    statisticalLabels.push({
      value: mean,
      text: `Mean: ${mean.toFixed(2)}`,
      color: '#ff4d4d',
      lineStyle: 'solid'
    });
  }

  // Combine user-provided labels with statistical labels
  const allLabels = [...labels, ...statisticalLabels];

  // Create mark lines for all labels
  const markLines = allLabels.map(label => ({
    name: label.text,
    xAxis: label.value,
    label: {
      formatter: label.text,
      position: 'insideEndTop' as const,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      padding: [4, 8],
      borderRadius: 4
    },
    lineStyle: {
      color: label.color || '#666',
      type: label.lineStyle || 'solid',
      width: 2
    }
  }));

  const lineSeries: LineSeriesOption = {
    type: 'line',
    data: lineData,
    smooth: true,
    showSymbol: false,
    lineStyle: {
      width: 2,
      color: '#8884d8'
    },
    areaStyle: {
      opacity: 0.1,
      color: '#8884d8'
    },
    markLine: {
      symbol: ['none', 'none'],
      data: markLines,
      emphasis: {
        lineStyle: {
          width: 3
        }
      }
    },
    clip: true
  };

  const option: EChartsOption = {
    title: {
      text: title,
      left: 'center',
      top: 10,
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      },
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderWidth: 1,
      borderColor: '#ccc',
      padding: 10,
      textStyle: {
        color: '#333'
      },
      formatter: (params: any) => {
        const x = params[0].data[0];
        const y = params[0].data[1];
        return `Value: ${x.toFixed(2)}<br/>Probability: ${(y * 100).toFixed(2)}%`;
      }
    },
    toolbox: {
      show: true,
      feature: {
        saveAsImage: {
          title: 'Save as Image',
          name: 'distribution-plot'
        },
        dataZoom: {
          title: {
            zoom: 'Zoom',
            back: 'Zoom Reset'
          }
        }
      }
    },
    grid: {
      top: 60,
      right: 40,
      bottom: 60,
      left: 60,
      containLabel: true
    },
    xAxis: {
      type: 'value',
      name: xAxisLabel,
      nameLocation: 'middle',
      nameGap: 30,
      scale: true,
      axisLine: {
        show: true
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed',
          opacity: 0.3
        }
      }
    },
    yAxis: {
      type: 'value',
      name: yAxisLabel,
      nameLocation: 'middle',
      nameGap: 40,
      min: 0,
      max: 1,
      axisLine: {
        show: true
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed',
          opacity: 0.3
        }
      },
      axisLabel: {
        formatter: (value: number) => `${(value * 100).toFixed(0)}%`
      }
    },
    series: [lineSeries]
  };

  return (
    <div style={{ height }} className={className}>
      <LazyTabLabECharts option={option} themeName="gentle-photon-stream" />
    </div>
  );
}

// For backward compatibility and when the distribution type is not specified
export const DistributionGraph = DiscreteDistributionGraph; 